import React from 'react';
import { useHistory } from 'react-router-dom';
import { IoChatbubbleEllipses, IoPersonAdd, IoPersonRemove, IoCheckmark, IoClose, IoBan, IoCreateOutline, IoArrowUndo, IoStar, IoStarOutline } from 'react-icons/io5';
import Avatar from '../components/Avatar';
import { bannerStyle } from './ProfileContent';
import { presenceLabelKey } from '../utils/presence';
import { useSocket } from '../socket/SocketContext';
import { useLanguage } from '../i18n/LanguageContext';
import { useConfirm } from '../hooks/useConfirm';
import { sendFriendRequest, cancelRequest, confirmRequest, ignoreFriendRequest, removeFriend, blockUser, unblockUser, setFavorite } from '../api/FriendsAPI';

const me = () => localStorage.getItem('username');
const token = () => localStorage.getItem('accessToken');

// Banner + avatar + name block shared by the card and the full profile.
// `p` is the loaded profile, or the seed the opener passed while it loads.
export function ProfileHeader({ p, big = false }) {
  const { statusOf } = useSocket();
  const { t } = useLanguage();
  const name = p.fullName || p.username;
  const status = statusOf(p.username);
  return (
    <>
      <div className="ProfileBanner" style={bannerStyle(p, { wide: big })} />
      <div className="ProfileHeadRow">
        <Avatar
          imageUrl={p.imageUrl}
          size={big ? 160 : 96}
          animate="always"
          alt={name}
          className="ProfileAvatar"
          zoom
        />
      </div>
      <div className="ProfileNames">
        <div className="ProfileName">{name}</div>
        <div className="ProfileHandle">@{p.username}</div>
        <div className="ProfileStatus">
          <span className={`ProfileStatusDot ProfileStatus-${status || 'offline'}`} />
          {t(presenceLabelKey(status))}
        </div>
      </div>
    </>
  );
}

export function formatMemberSince(date, locale) {
  if (!date) return '';
  return new Date(date).toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
}

// The actions for the viewer's relationship to this person. Every change
// reloads the profile (onChanged), so the buttons follow the new state.
// compact (the card): only the main action keeps its label, the rest are
// icons with a tooltip - a 320px card can't fit three labelled buttons.
export function ProfileActions({ profile, onChanged, onNavigate, compact = false }) {
  const { t } = useLanguage();
  const { pushToast } = useSocket();
  const history = useHistory();
  const [confirm, confirmEl] = useConfirm();
  const name = profile.fullName || profile.username;
  const target = profile.username;

  const run = async (call) => {
    const res = await call().catch(() => ({ status: 'fail' }));
    if (res.status !== 'success') pushToast(res.message || t('profile.actionFailed'));
    onChanged();
  };

  const message = () => {
    onNavigate();
    history.push('/home/', { openChatWith: { username: profile.username, fullName: profile.fullName, imageUrl: profile.imageUrl } });
  };

  const btn = (key, icon, label, onClick, color = 'secondaryColor') => {
    // 'besidePrimary': a secondary action next to a labelled main one - the
    // one that turns into an icon on the compact card.
    const besidePrimary = color.includes('besidePrimary');
    const base = color.replace('besidePrimary', '').trim();
    const iconOnly = base.includes('ProfileActionIcon') || (compact && besidePrimary);
    const cls = iconOnly && !base.includes('ProfileActionIcon') ? `${base} ProfileActionIcon` : base;
    return (
      <button key={key} type="button" className={`ProfileActionBtn ${cls}`} onClick={onClick} title={iconOnly ? label : undefined} aria-label={label}>
        {icon}<span>{label}</span>
      </button>
    );
  };

  let buttons = [];
  switch (profile.relationship) {
    case 'self':
      buttons = [btn('edit', <IoCreateOutline />, t('profile.editProfile'), () => { onNavigate(); history.push('/home/settings'); }, 'primaryColor')];
      break;
    case 'friend':
      buttons = [
        btn('msg', <IoChatbubbleEllipses />, t('common.sendMessage'), message, 'primaryColor'),
        btn('remove', <IoPersonRemove />, t('profile.removeFriend'), () => confirm({
          message: t('profile.removeConfirm', { name }), confirmLabel: t('profile.removeFriend'),
          onConfirm: () => run(() => removeFriend(me(), token(), target))
        }), 'secondaryColor besidePrimary'),
        btn('favorite', profile.favorite ? <IoStar /> : <IoStarOutline />,
          profile.favorite ? t('profile.unfavorite') : t('profile.favorite'),
          () => run(() => setFavorite(target, !profile.favorite)),
          `secondaryColor ProfileActionIcon${profile.favorite ? ' ProfileFavoriteOn' : ''}`)
      ];
      break;
    case 'incoming':
      buttons = [
        btn('accept', <IoCheckmark />, t('profile.accept'), () => run(() => confirmRequest(me(), token(), target)), 'primaryColor'),
        btn('decline', <IoClose />, t('profile.decline'), () => run(() => ignoreFriendRequest(me(), token(), target)), 'secondaryColor besidePrimary')
      ];
      break;
    case 'outgoing':
      buttons = [btn('cancel', <IoArrowUndo />, t('profile.cancelRequest'), () => run(() => cancelRequest(me(), token(), target)))];
      break;
    case 'blocked':
      buttons = [btn('unblock', <IoBan />, t('profile.unblock'), () => run(() => unblockUser(me(), token(), target)))];
      break;
    default:
      buttons = [btn('add', <IoPersonAdd />, t('profile.addFriend'), () => run(() => sendFriendRequest(me(), token(), target)), 'primaryColor')];
  }
  if (!['self', 'blocked'].includes(profile.relationship)) {
    buttons.push(btn('block', <IoBan />, t('profile.block'), () => confirm({
      message: t('profile.blockConfirm', { name }), confirmLabel: t('profile.block'),
      onConfirm: () => run(() => blockUser(me(), token(), target))
    }), 'cancelColor ProfileActionIcon'));
  }

  return (
    <>
      <div className="ProfileActions">{buttons}</div>
      {confirmEl}
    </>
  );
}
